import { useEffect, useState } from 'react';

/**
 * A piece of state mirrored into a URL query param and kept in sync with
 * Back/Forward. `parse` turns the raw param (null when absent) into the
 * value; `serialize` returns the string to write, or null to drop the
 * param.
 */
export function useUrlParam<T>(
   key: string,
   initial: T,
   parse: (raw: string | null) => T,
   serialize: (value: T) => string | null
): [T, (value: T, opts?: { replace?: boolean }) => void] {
   const [value, setValue] = useState<T>(initial);
   useEffect(() => {
      const onPopState = () =>
         setValue(parse(new URLSearchParams(window.location.search).get(key)));
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
      // parse is captured at mount; key (a literal) is the stable identity.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [key]);
   const set = (next: T, opts: { replace?: boolean } = {}) => {
      const url = new URL(window.location.href);
      const serialized = serialize(next);
      if (serialized === null) {
         url.searchParams.delete(key);
      } else {
         url.searchParams.set(key, serialized);
      }
      if (opts.replace) {
         window.history.replaceState(null, '', url.toString());
      } else {
         window.history.pushState(null, '', url.toString());
      }
      setValue(next);
   };
   return [value, set];
}

export const parsePageParam = (raw: string | null): number => {
   const parsed = raw ? Number.parseInt(raw, 10) : 1;
   return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * Pagination backed by the ?page= query param (dropped at page 1), so
 * pages deep-link and back/forward walk them like a product listing.
 */
export function usePageParam(): [number, (page: number, opts?: { replace?: boolean }) => void] {
   return useUrlParam<number>(
      'page',
      parsePageParam(new URLSearchParams(window.location.search).get('page')),
      parsePageParam,
      page => (page <= 1 ? null : String(page))
   );
}
